import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  Banknote,
  CheckCircle2,
  ClipboardList,
  FileWarning,
  RefreshCw,
  Scale,
  ServerCog,
  WalletCards,
} from 'lucide-react';
import { Badge, Button, Card, CardHeader, Input, Tabs, TabsContent, TabsList, TabsTrigger, Textarea } from '@/components/ui';
import { companyFinanceApi } from '@/lib/api/companyFinance';
import { cn } from '@/lib/cn';
import type {
  CompanyFinanceException,
  CompanyFinanceHealthResponse,
  CompanyFinanceHealthStatus,
  CompanyFinanceOverview,
  CompanyFinanceSeverity,
} from '@/types/companyFinance';

const yuan = (cents: number) =>
  (cents / 100).toLocaleString('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    maximumFractionDigits: 2,
  });

export function CompanyFinancePage() {
  const queryClient = useQueryClient();
  const [periodInput, setPeriodInput] = useState('');
  const [periodFilter, setPeriodFilter] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState('');

  const overviewQuery = useQuery({
    queryKey: ['company-finance', 'overview', periodFilter ?? 'all'],
    queryFn: () => companyFinanceApi.getOverview(periodFilter),
  });
  const healthQuery = useQuery({
    queryKey: ['company-finance', 'health'],
    queryFn: () => companyFinanceApi.getHealth(true),
  });

  const reconcileMutation = useMutation({
    mutationFn: (payload: { period?: string; reason: string }) => companyFinanceApi.runReconciliation(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['company-finance'] });
    },
  });

  const overview = overviewQuery.data;
  const canRunReconciliation = reason.trim().length > 0 && !reconcileMutation.isPending;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <Scale className="mt-1 h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold text-foreground">公司财务</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>期间 {overview?.period ?? periodFilter ?? '全部'}</span>
              {overview && (
                <>
                  <span>·</span>
                  <span>生成 {formatDateTime(overview.generated_at)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-[140px_minmax(220px,1fr)_auto_auto]">
          <Input
            aria-label="期间"
            label="期间"
            placeholder="YYYY-MM"
            value={periodInput}
            onChange={(event) => setPeriodInput(event.target.value)}
          />
          <Textarea
            aria-label="对账原因"
            label="对账原因"
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setPeriodFilter(periodInput.trim() || undefined)}
              disabled={overviewQuery.isFetching}
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', overviewQuery.isFetching && 'animate-spin')} />
              刷新
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              className="w-full"
              loading={reconcileMutation.isPending}
              disabled={!canRunReconciliation}
              onClick={() =>
                reconcileMutation.mutate({
                  period: periodInput.trim() || undefined,
                  reason: reason.trim(),
                })
              }
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              运行对账
            </Button>
          </div>
        </div>
      </div>

      {overviewQuery.isLoading && <div className="rounded-lg border border-border p-6 text-muted-foreground">加载中...</div>}
      {overviewQuery.error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-foreground">
          {getErrorMessage(overviewQuery.error)}
        </div>
      )}
      {reconcileMutation.error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-foreground">
          {getErrorMessage(reconcileMutation.error)}
        </div>
      )}
      {reconcileMutation.data && (
        <div className="rounded-lg border border-success bg-success/10 p-4 text-sm text-foreground">
          已写入 {reconcileMutation.data.persisted_exception_count} 条异常 · 触发人 {reconcileMutation.data.triggered_by}
        </div>
      )}
      {healthQuery.isLoading && <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">健康检查加载中...</div>}
      {healthQuery.error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-foreground">
          {getErrorMessage(healthQuery.error)}
        </div>
      )}
      {healthQuery.data && <HealthPanel health={healthQuery.data} />}

      {overview && <CompanyFinanceContent overview={overview} />}
    </div>
  );
}

function HealthPanel({ health }: { health: CompanyFinanceHealthResponse }) {
  const failedTableProbes = health.table_probes.filter((probe) => probe.error);
  const okTableProbeCount = health.table_probes.filter((probe) => probe.status === 'ok').length;
  return (
    <Card>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <ServerCog className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-medium text-foreground">生产健康检查</h2>
              <Badge variant={healthStatusVariant(health.status)}>{health.status}</Badge>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">检查 {formatDateTime(health.checked_at)}</div>
          </div>
        </div>
        <div className="grid gap-2 text-sm sm:grid-cols-2 lg:min-w-[460px]">
          <HealthLine label="App" value={health.app_credentials.status} />
          <HealthLine label="Base" value={health.config.base_url || '-'} />
          <HealthLine label="配置" value={`${health.config.configured_table_count}/${health.config.expected_table_count}`} />
          <HealthLine label="投递" value={health.delivery.status} />
          <HealthLine label="复核人" value={`${health.reviewers.legal_reviewer_count}`} />
          <HealthLine label="表探针" value={`${okTableProbeCount}/${health.table_probes.length}`} />
          <HealthLine label="读探针" value={`${health.live_read_probe.status} · 表 ${health.live_read_probe.checked_table_count}`} />
          <HealthLine label="写探针" value={health.write_probe.status} />
        </div>
      </div>
      {health.config.error && <HealthAlert tone="destructive" label="配置异常" value={health.config.error} />}
      {health.missing_env.length > 0 && (
        <HealthAlert tone="warning" label="缺失配置" value={health.missing_env.join(', ')} />
      )}
      {health.live_read_probe.error && (
        <HealthAlert tone="destructive" label="读探针失败" value={health.live_read_probe.error} />
      )}
      {health.write_probe.error && (
        <HealthAlert tone="warning" label="写探针状态" value={health.write_probe.error} />
      )}
      {failedTableProbes.length > 0 && (
        <div className="mt-3 space-y-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-foreground">
          {failedTableProbes.map((probe) => (
            <div className="break-all" key={`${probe.key}-${probe.table_id ?? 'missing'}`}>
              表探针失败 {probe.key} {probe.table_id ?? '-'} {probe.error}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function HealthLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      {' '}
      <span className="ml-2 break-all font-medium text-foreground">{value}</span>
    </div>
  );
}

function HealthAlert({ tone, label, value }: { tone: 'warning' | 'destructive'; label: string; value: string }) {
  return (
    <div
      className={cn(
        'mt-3 break-all rounded-md border p-3 text-sm text-foreground',
        tone === 'warning' && 'border-warning bg-warning/10',
        tone === 'destructive' && 'border-destructive bg-destructive/10'
      )}
    >
      {label} {value}
    </div>
  );
}

function healthStatusVariant(status: CompanyFinanceHealthStatus): 'success' | 'warning' | 'destructive' {
  if (status === 'healthy') return 'success';
  if (status === 'permission_denied' || status === 'probe_failed') return 'destructive';
  return 'warning';
}

function CompanyFinanceContent({ overview }: { overview: CompanyFinanceOverview }) {
  const exceptionTable = overview.config.tables.find((table) => table.key === 'reconciliation_exceptions');
  const latest = overview.latest_reconciliation;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <RiskCard
          title="Apple 外汇待入账"
          value={yuan(overview.risk_summary.apple_fx_pending_cny_cents)}
          icon={Banknote}
          tone={overview.risk_summary.apple_fx_pending_cny_cents > 0 ? 'warning' : 'success'}
        />
        <RiskCard
          title="投资人未实缴"
          value={yuan(overview.risk_summary.investor_outstanding_cny_cents)}
          icon={WalletCards}
          tone={overview.risk_summary.investor_outstanding_cny_cents > 0 ? 'warning' : 'success'}
        />
        <RiskCard
          title="挂靠/非公司往来"
          value={yuan(overview.risk_summary.non_company_clearing_balance_cny_cents)}
          icon={FileWarning}
          tone={overview.risk_summary.non_company_clearing_balance_cny_cents !== 0 ? 'warning' : 'success'}
        />
        <RiskCard
          title="待办与复核"
          value={`${overview.risk_summary.open_compliance_tasks} 项`}
          icon={ClipboardList}
          tone={overview.risk_summary.open_compliance_tasks > 0 ? 'info' : 'success'}
        />
      </div>

      <Card>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="success">Feishu Base live-read</Badge>
              <Badge variant={latest.status === 'clean' ? 'success' : 'warning'}>
                {latest.status === 'clean' ? '无待处理异常' : '需要处理'}
              </Badge>
              <span className="text-sm text-muted-foreground">
                活跃 {latest.open_exception_count} · Critical {latest.critical_exception_count} · Warning {latest.warning_exception_count}
              </span>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              最近异常 {latest.last_exception_created_at ? formatDateTime(latest.last_exception_created_at) : '-'}
            </div>
          </div>
          {exceptionTable?.url && (
            <a
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
              href={exceptionTable.url}
              target="_blank"
              rel="noreferrer"
            >
              打开飞书异常表
              <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
        </div>
      </Card>

      <Tabs defaultValue="exceptions">
        <TabsList>
          <TabsTrigger value="exceptions">异常</TabsTrigger>
          <TabsTrigger value="snapshot">台账</TabsTrigger>
          <TabsTrigger value="config">配置</TabsTrigger>
        </TabsList>
        <TabsContent value="exceptions" className="mt-4">
          <ExceptionsTable exceptions={overview.exceptions} />
        </TabsContent>
        <TabsContent value="snapshot" className="mt-4">
          <SnapshotGrid overview={overview} />
        </TabsContent>
        <TabsContent value="config" className="mt-4">
          <ConfigTable overview={overview} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RiskCard({
  title,
  value,
  icon: Icon,
  tone,
}: {
  title: string;
  value: string;
  icon: typeof Scale;
  tone: 'success' | 'warning' | 'info';
}) {
  const toneClass = tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : 'text-info';
  return (
    <Card className="p-4" padding={false}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm text-muted-foreground">{title}</div>
          <div className="mt-2 text-xl font-semibold text-foreground">{value}</div>
        </div>
        <Icon className={cn('h-5 w-5', toneClass)} />
      </div>
    </Card>
  );
}

function ExceptionsTable({ exceptions }: { exceptions: CompanyFinanceException[] }) {
  return (
    <Card padding={false}>
      <CardHeader className="border-b border-border px-4 py-3 pb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <h2 className="font-medium text-foreground">对账异常</h2>
        </div>
      </CardHeader>
      {exceptions.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground">暂无异常</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="border-b border-border bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">类型</th>
                <th className="px-4 py-3 font-medium">级别</th>
                <th className="px-4 py-3 font-medium">状态</th>
                <th className="px-4 py-3 text-right font-medium">金额</th>
                <th className="px-4 py-3 font-medium">建议动作</th>
                <th className="px-4 py-3 font-medium">关联记录</th>
                <th className="px-4 py-3 font-medium">飞书</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {exceptions.map((exception) => (
                <tr key={exception.record_id || `${exception.exception_type}-${exception.suggested_action}`}>
                  <td className="px-4 py-3 font-medium text-foreground">{exception.exception_type}</td>
                  <td className="px-4 py-3">
                    <SeverityBadge severity={exception.severity} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{exception.status}</td>
                  <td className="px-4 py-3 text-right font-mono text-foreground">{yuan(exception.amount_cny_cents)}</td>
                  <td className="max-w-[320px] px-4 py-3 text-foreground">{exception.suggested_action}</td>
                  <td className="px-4 py-3 text-muted-foreground">{exception.linked_records.join(', ') || '-'}</td>
                  <td className="px-4 py-3">
                    {exception.table_url ? (
                      <a
                        className="inline-flex items-center gap-1 font-medium text-primary hover:text-primary/80"
                        href={exception.table_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        打开
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function SnapshotGrid({ overview }: { overview: CompanyFinanceOverview }) {
  const snapshot = overview.snapshot;
  const items = [
    ['企业现金', yuan(snapshot.company_cash_cny_cents)],
    ['平台销售', yuan(snapshot.platform_sales_cny_cents)],
    ['平台结算', yuan(snapshot.platform_settlement_cny_cents)],
    ['企业银行已收经营收入', yuan(snapshot.bank_received_platform_cny_cents)],
    ['未开票收入', yuan(snapshot.unbilled_revenue_cny_cents)],
    ['申报销售额', yuan(snapshot.reported_sales_cny_cents)],
    ['投资人承诺', yuan(snapshot.investor_committed_cny_cents)],
    ['投资人实缴', yuan(snapshot.investor_paid_cny_cents)],
    ['进公司账户', yuan(snapshot.investor_company_account_cny_cents)],
    ['直接购资产', yuan(snapshot.investor_direct_asset_cny_cents)],
    ['工资应发', yuan(snapshot.payroll_gross_cny_cents)],
    ['工资公司成本', yuan(snapshot.payroll_company_cost_cny_cents)],
  ];
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {items.map(([label, value]) => (
        <Card key={label} className="p-4" padding={false}>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-1 truncate text-lg font-semibold text-foreground">{value}</div>
        </Card>
      ))}
    </div>
  );
}

function ConfigTable({ overview }: { overview: CompanyFinanceOverview }) {
  return (
    <Card padding={false}>
      <CardHeader className="border-b border-border px-4 py-3 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-medium text-foreground">飞书 Base</h2>
            <p className="mt-1 text-sm text-muted-foreground">{overview.config.base_url}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="success">Feishu Base live-read</Badge>
            <StatusBadge active={overview.config.openapi_sender_configured} label="应用发卡" />
            <StatusBadge active={overview.config.websocket_configured} label="WebSocket" />
            <Badge variant="info">复核人 {overview.config.legal_reviewer_count}</Badge>
          </div>
        </div>
      </CardHeader>
      <div className="border-b border-border px-4 py-3 text-sm text-muted-foreground">
        数据源 {overview.sync_status.status} · 表 {overview.sync_status.configured_table_count} · 检查 {formatDateTime(overview.sync_status.checked_at)}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">表</th>
              <th className="px-4 py-3 font-medium">table_id</th>
              <th className="px-4 py-3 font-medium">链接</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {overview.config.tables.map((table) => (
              <tr key={table.key}>
                <td className="px-4 py-3 font-medium text-foreground">{table.key}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{table.table_id}</td>
                <td className="px-4 py-3">
                  {table.url ? (
                    <a
                      className="inline-flex items-center gap-1 font-medium text-primary hover:text-primary/80"
                      href={table.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      打开飞书
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SeverityBadge({ severity }: { severity: CompanyFinanceSeverity }) {
  const variant = severity === 'critical' ? 'destructive' : severity === 'warning' ? 'warning' : 'info';
  return <Badge variant={variant}>{severity}</Badge>;
}

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <Badge variant={active ? 'success' : 'outline'}>
      {active ? <CheckCircle2 className="h-3 w-3" /> : null}
      {label}
    </Badge>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
